/** Contacts page strings (extracted to keep en.ts under the size ratchet). */
import { enEmailProvenance } from "./enEmailProvenance";
import { enEmailSuppression } from "./enEmailSuppression";
import { enLeadPanel } from "./enLeadPanel";

export const enContacts = {
	list: "List",
	title: "Contacts",
	description: "Manage all your contacts, companies, competitors, and blacklisted organizations.",
	close: "Close",
	selectColumns: "Select columns",
	filter: "Filter",
	searchPlaceholder: "Search contacts...",
	sendMessage: "Send a message",
	tabs: {
		contacts: "Contacts",
		companies: "Companies",
		competitors: "Competitors",
		blacklist: "Blacklist",
		suppressed: "Suppressed",
		doNotContact: "Do-not-contact",
	},
	leadPanel: enLeadPanel,
	emailProvenance: enEmailProvenance,
	suppressed: enEmailSuppression,
	competitors: {
		explainer:
			"Companies marked as competitors are excluded from every list and every search for your whole account, permanently — until you remove them here.",
		unflagColumn: "Competitor",
		reasonColumn: "Reason",
		detectedAtColumn: "Detected",
		addAction: "Add competitors",
		addTitle: "Add competitors",
		addSubmit: "Add",
		addLabel: "Company domains or LinkedIn company URLs",
		addHint:
			"One per line. A company already in your account is marked as a competitor everywhere; anything else is blocked account-wide on its address alone and appears on the Blacklist tab until a matching company is found.",
		addUploadHint: "…or drop a CSV here (domain / website / LinkedIn company URL columns)",
		addSummary:
			"{{matched}} marked as competitors, {{blocked}} blocked without a company yet, {{duplicates}} already blocked.",
		addInvalid: "Not usable and skipped: {{entries}}",
		unflagAction: "Not a competitor",
		unflagTitle: "Not a competitor",
		unflagConfirm: "Remove from competitors",
		unflagIntroOne: "{{name}} will no longer be treated as a competitor.",
		unflagIntroMany: "{{count}} companies will no longer be treated as competitors.",
		unflagEffectBlacklist: "They are removed from the blacklist and can be contacted again.",
		unflagEffectLeads: "Their leads come back into the lists they were excluded from.",
		unflagEffectFuture: "Lead scoring will not mark them as competitors again.",
		unflagCategoryLabel: "Move them to this company category",
		unflagNoCategories: "No company categories are configured yet — set up lead scoring first.",
		unflagDone: "{{count}} company/companies removed from competitors",
		reflagDone: "Marked as competitors again",
		undo: "Undo",
	},
	blacklist: {
		company: "Company",
		global: "Global",
		addEntry: "Add Entry",
		upload: "Upload CSV",
		createTitle: "Create Blacklist Entry",
		deleteTitle: "Delete Blacklist Entry",
		deleteConfirmation: "Are you sure you want to delete this blacklist entry?",
		deleted: "Blacklist entry deleted successfully",
		domain: "Domain",
		email: "Email",
		linkedinUrl: "LinkedIn URL",
		linkedinCompanyUrl: "LinkedIn Company URL",
		scope: "Scope",
		scopeAuto: "Auto (infer from data)",
		scopePerson: "Person",
		scopeOrganization: "Organization",
		source: "Source",
		scopeColumn: "Scope",
		detectedBy: "Detected by",
		detectedByAi: "AI-detected",
		detectedByManual: "Manual",
		detectedByLead: "By the lead",
		addedBy: "Added by",
		removeColumn: "Remove",
		removeAction: "Remove from blacklist",
		// PREC-2548: an unsubscribe is a compliance record and has no way off the blacklist.
		removeNotAllowedUnsubscribed: "Unsubscribed",
		removeNotAllowedUnsubscribedHint: "The lead unsubscribed, so this entry cannot be removed.",
		keptUnsubscribed: "Kept on the blacklist because they unsubscribed: {{names}}",
		sourcePerson: "Person",
		sourceOrganization: "Organization",
		sourceList: "List exclusion",
		notEnrichedYet: "Not enriched yet",
		snoozed: "Snoozed",
		snoozedYes: "Yes",
		snoozedUntil: "Snoozed Until",
		addedToBlacklistAt: "Added to Blacklist",
		origin: "Origin",
		originUnsubscribed: "Unsubscribed by lead",
		originBlacklistedByUser: "Blacklisted by user",
		originImportedByUser: "Imported by user",
		originCompetitor: "Competitor",
		originUnknown: "Unknown",
		originByUser: "by {{name}}",
		empty: "No blacklisted contacts found.",
		importBlacklist: "Import Blacklist",
		delete: "Delete",
		removeTitle: "Remove from Blacklist",
		removeConfirmation:
			"Are you sure you want to remove {{count}} selected prospect(s) from the blacklist? The removal will be based on the current source type (Person or Organization) of each entry. Leads who unsubscribed stay on the blacklist.",
		removeOrganizationToo: "Also remove organization from blacklist",
		snoozeTitle: "Snooze Prospects",
		snoozeAction: "Snooze",
		snoozeConfirmation: "Set or update the snooze date for {{count}} selected prospect(s).",
		unsnoozeTitle: "Unsnooze Prospects",
		unsnoozeAction: "Unsnooze",
		unsnoozeConfirmation: "Are you sure you want to unsnooze {{count}} selected prospect(s)?",
		uploadTitle: "Upload Blacklist CSV",
		downloadExample: "Download Example CSV",
		dragAndDrop: "Drag and drop CSV file here or click to upload",
		uploadAsGlobal: "Import as Global (Admin only)",
	},
	table: {
		name: "Name",
		email: "Email",
		company: "Company",
		seniority: "Seniority",
		location: "Location",
		city: "City",
		state: "State",
		linkedin: "LinkedIn",
		industry: "Industry",
		employees: "Employees",
		founded: "Founded",
		website: "Website",
		prospects: "Prospects",
		reason: "Reason",
		jobTitle: "Job Title",
		gender: "Gender",
		genderConfirmOption: "Confirm",
		genderConfirmedTooltip: "Gender is confirmed.",
		genderUnsureTooltip:
			"Gender confidence is below 90%. Please change or confirm this gender to avoid wrong salutations.",
		genderSetTooltip: "Set gender",
		salutationDe: "Salutation (DE)",
		formalNameDe: "Formal Name (DE)",
		salutationEn: "Salutation (EN)",
		personalCountry: "Country",
		companyCountry: "Company Country",
		companyCity: "Company City",
		companyState: "Company State",
		companyIndustry: "Company Industry",
		normalizedCompanyName: "Normalized Company Name",
		companySize: "Company Size",
		companyFoundedYear: "Founded Year",
		companyCategory: "Company Segment",
		personaCategory: "Persona Segment",
		leadScoring: "Lead Score",
		lists: "Lists",
		originList: "List of Origination",
		profileTracker: "Profile Tracker",
		lastAssignStatus: "Last Assign Status",
		lastSentiment: "Last Sentiment",
		firstSentiment: "First Sentiment",
		bestSentiment: "Best Sentiment",
		lastContactAt: "Last Contact",
		lastResponseAt: "Last Response",
		headline: "Headline",
		about: "About",
		language: "Language",
		connections: "Connections",
		followers: "Followers",
		reportedEmployees: "Reported Employees",
		roleChange: "Role Change",
		previousEmployers: "Previous Employers",
		previousEmployer: "Previous Employer",
		previousJobTitle: "Previous Job Title",
		createdAt: "Created At",
		research: "Research",
		personaScore: "Persona Priority",
		companyScore: "Company Priority",
	},
	addTo: "Add to",
	enrich: "Enrich",
	searchForEmail: "Search for Email",
	searchForPhone: "Search for Phone",
	globalSearchEmailCost: "Search for email — 2 credits",
	globalSearchPhoneCost: "Search for phone — 11 credits",
	globalSearchEnrichNote: "1 of these credits enriches the person.",
	globalSearchAvailableNote:
		"The person is then available in Contacts and can be added to lists from there free of charge.",
	select: "Select contacts",
	all: "All contacts",
	currentPage: "Current page",
	addedToList_one:
		"Adding {{count}} contact to {{name}}. Processing can take up to 5 minutes to finish.",
	addedToList_other:
		"Adding {{count}} contacts to {{name}}. Processing can take up to 5 minutes to finish.",
	addedToCompose: "Contact added to Compose",
	// PREC-4595: the result of "Add to List" on a list, built from what the server answered
	// (`added`, `skipped`), never from the selection's size. A move removes every selected lead from
	// this list, including those the destination already held.
	transferResult: {
		added_one: "{{count}} contact added to {{name}}.",
		added_other: "{{count}} contacts added to {{name}}.",
		moved_one: "{{count}} contact moved to {{name}}.",
		moved_other: "{{count}} contacts moved to {{name}}.",
		noneAdded: "No contacts added to {{name}}.",
		alreadyThere_one: "{{count}} was already there.",
		alreadyThere_other: "{{count}} were already there.",
		alreadyThereRemoved_one: "{{count}} was already there and was removed from this list.",
		alreadyThereRemoved_other: "{{count}} were already there and were removed from this list.",
		// The answer held no counts: say what happened without claiming a number.
		addedUnknown: "Added to {{name}}.",
		movedUnknown: "Moved to {{name}}.",
		createdEmpty: "List {{name}} created. No contacts were added.",
		notAdded_one:
			"{{count}} could not be added: it is no longer on this list or has no contact record.",
		notAdded_other:
			"{{count}} could not be added: they are no longer on this list or have no contact record.",
		notMoved_one:
			"{{count}} could not be moved: it is no longer on this list or has no contact record.",
		notMoved_other:
			"{{count}} could not be moved: they are no longer on this list or have no contact record.",
		companiesWithoutContacts: "The selected companies have no contacts.",
		// Contacts → Add to List queues lead scoring for every contact it adds.
		processingNote: "Processing can take up to 5 minutes to finish.",
	},
	addToListModeKeep: "Keep on current list (copy)",
	addToListModeKeepDescription: "Add to the selected list and keep them on this list.",
	addToListModeMove: "Move (remove from this list)",
	addToListModeMoveDescription: "Add to the selected list and remove them from this list.",
	addToTracker: "Add to Tracker",
	addToTrackerTitle: "Add to Profile Tracker",
	addedToTracker: "Contacts added to tracker",
	addedCompanyToTracker: "Company added to tracker",
	trackerList: "Select Tracker",
	searchTracker: "Search tracker...",
	confirmAddToList: "Add contacts to list",
	confirmAddToListMessage: 'Are you sure you want to add {{count}} contacts to "{{name}}"?',
	confirmCreateAndAddToList: "Create list & add contacts",
	confirmCreateAndAddToListMessage:
		'A new list "{{name}}" will be created and {{count}} contacts will be added to it. Continue?',
	// PREC-4595: a Companies or Competitors selection counts companies; the list receives their people.
	confirmAddCompaniesToListMessage_one:
		'Are you sure you want to add the contacts of {{count}} company to "{{name}}"?',
	confirmAddCompaniesToListMessage_other:
		'Are you sure you want to add the contacts of {{count}} companies to "{{name}}"?',
	confirmCreateAndAddCompaniesToListMessage_one:
		'A new list "{{name}}" will be created and the contacts of {{count}} company will be added to it. Continue?',
	confirmCreateAndAddCompaniesToListMessage_other:
		'A new list "{{name}}" will be created and the contacts of {{count}} companies will be added to it. Continue?',
	addToBlacklist: "Add to Blacklist",
	addToBlacklistTitle: "Add to Blacklist",
	// PREC-4356: an entry can be removed again under Do Not Contact → Blacklist, so say where.
	addToBlacklistConfirmation:
		"Are you sure you want to add {{count}} selected contact(s) to the blacklist? They will not be contacted again. You can remove them later under Do Not Contact → Blacklist.",
	addEntireCompanyToBlacklist: "Also add their entire company to blacklist",
	blacklistImpactChecking: "Checking impact…",
	blacklistImpactOnList_one: "{{count}} of the selected contacts is currently on a list.",
	blacklistImpactOnList_other: "{{count}} of the selected contacts are currently on lists.",
	blacklistImpactActiveCampaign_one:
		"{{count}} is in an active campaign — blacklisting will stop their campaign.",
	blacklistImpactActiveCampaign_other:
		"{{count}} are in active campaigns — blacklisting will stop their campaigns.",
	blacklistImpactLists: "Lists affected: {{names}}",
	addedToBlacklist: "Contact(s) added to blacklist",
	enrichEmailStarted: "Email search started",
	enrichPhoneStarted: "Phone search started",
	// PREC-3953: the export is built in the background and announced by a notification.
	exportStarted: "Export started. We'll notify you when your file is ready to download.",
	createNewList: "Create new list",
	searchList: "Search list...",
	newListName: "New list name",
	smartList: {
		createSmartList: "Create Smart List",
		createSmartListTitle: "Create Smart List (Recurring)",
		listName: "List Name",
		listNamePlaceholder: "Enter smart list name",
		filterQuery: "Filter Query",
		editFilters: "Edit",
		activeFilters: "{{count}} active filter(s)",
		semanticSearch: "Semantic search: {{query}}",
		updateFrequency: "Update Frequency",
		infoBanner:
			"This list will automatically update based on your filter criteria. Contacts matching the criteria will be added, and those no longer matching will be removed.",
		created: "Smart list '{{name}}' created successfully",
		DAILY: "Daily",
		WEEKLY: "Weekly",
		MONTHLY: "Monthly",
		filterTitle: "Define Smart List Filters",
		filterDescription:
			"Configure the filters that determine which contacts are included in this Smart List. The list will automatically update based on these criteria.",
		semanticSearchTitle: "Semantic Search",
		semanticSearchDescription:
			"Use natural language to describe the type of prospects you are looking for. This will be used in addition to the filters above.",
		semanticSearchPlaceholder: "e.g. AI engineers working at startups in Berlin",
		relativeToday: "today",
		relativeDaysAgo: "{{count}} day ago",
		relativeDaysAgo_other: "{{count}} days ago",
		relativeDaysFromNow: "in {{count}} day",
		relativeDaysFromNow_other: "in {{count}} days",
	},
	semanticSearchProspectsLabel: "Semantic search prospects",
	semanticSearchProspectsPlaceholder: "e.g. AI engineers in Berlin",
	semanticSearchOrganizationsLabel: "Semantic search organizations",
	semanticSearchOrganizationsPlaceholder: "e.g. SaaS companies with 50+ employees",
	settings: {
		title: "Contacts & Enrichment",
		description: "Configure automated contact management features.",
		workspaceWide: "Workspace-wide",
		overridablePerList: "Overridable per list",
		loadError: "The Contacts & Enrichment settings could not be loaded.",
		retry: "Retry",
		autoArchive: {
			title: "Auto-Archive Disqualified Contacts",
			description:
				"Automatically archive (soft-delete) disqualified contacts that have not been contacted in the last X months.",
			enabled: "Enable auto-archive",
			months: "Months without contact",
			monthsPlaceholder: "e.g. 6",
		},
		reEnrichment: {
			title: "Auto Re-Enrichment of Qualified Leads",
			description:
				"Automatically re-enrich all qualified leads every 12 months to check for significant job changes and re-do lead scoring. Costs: 0.2 credits per lead.",
			enabled: "Enable auto re-enrichment",
		},
		emailVerification: {
			title: "Email verification",
			description:
				"Check that an address can receive mail before it enters a sequence. This covers addresses that did not come from Signalist's enrichment.",
		},
		riskyEmails: {
			title: "Risky emails",
			description:
				"Some addresses cannot be confirmed either way, most often because the receiving domain accepts everything. Sending them widens your reach but puts your sending reputation at risk.",
			recommended: "Recommended",
			policy: {
				NEVER: "Never send risky emails",
				AUTO: "Let Signalist decide",
				ALWAYS: "Always send risky emails",
			},
			policyDescription: {
				NEVER: "Maximum protection. Risky addresses are held and never sent.",
				AUTO: "Risky addresses are sent while the sending mailbox is healthy and held automatically when it is not.",
				ALWAYS: "Maximum coverage. Use only with a warmed, established mailbox.",
			},
			pauseAbove4Percent: "Pause the mailbox when its bounce rate goes above 4%",
			pauseAbove4PercentDescription:
				"Above 4% the problem is usually the whole list, not only the risky addresses. If this is off, only risky addresses are held.",
		},
		bounceRepair: {
			title: "Bounce repair",
			description:
				"When an email bounces because the mailbox does not exist, Signalist stops using that address, looks for a working one for the same person, and sends the step that bounced again.",
			reviewOnCompanyChange: "Send to Review when the person changed company",
			reviewOnCompanyChangeDescription:
				"The drafted message still names their old employer, so it waits in Review instead of going out. Repairs at the same company are sent again automatically.",
		},
		listDeliverability: {
			title: "Email deliverability",
			description:
				"Email verification and risky addresses for this list. Leave a setting on the workspace default unless this list needs something else.",
			on: "On",
			off: "Off",
			workspaceDefaultOption: "Workspace default (currently: {{value}})",
			workspaceDefaultOptionUnknown: "Workspace default",
			overridesWorkspaceDefault: "Overrides the workspace default ({{value}})",
			useWorkspaceDefault: "Use workspace default",
			verificationTitle: "Email verification",
			verificationDescription:
				"Verify addresses that came from an upload, a CRM sync or manual entry before the first email. Addresses Signalist enriches are always verified.",
			riskyPolicyTitle: "Risky emails",
			riskyPolicyDescription:
				"What happens to addresses that cannot be confirmed either way (catch-all domains).",
			workspaceWideHeading: "Workspace-wide, the same for every list",
			bounceRepairTitle: "Bounce repair",
			bounceRepairOnWithReview: "On, at the same company the step is sent again automatically",
			bounceRepairOn: "On, the step that bounced is sent again automatically",
			bounceRepairOff: "Off",
			suppressionTitle: "Suppression",
			suppressionSummary: "A bounced address is never contacted again, from any list",
			changeInCompanySettings: "Change in Company Settings",
			companySettingsAccessOnly: "Only users with Company Settings access can change this",
		},
		saved: "Settings saved successfully",
		saveError: "Failed to save settings",
		save: "Save",
	},
};
